/**
 * Subscription Cancel Page
 *
 * Landing page when user cancels Stripe checkout.
 * Redirects back to the subscription page.
 */

import React, { useEffect } from 'react';
import { View, Text } from 'react-native';
import { useRouter } from 'expo-router';
import { X } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { track, useTrackScreen } from '@/lib/analytics';
import { useProfileStore } from '@/lib/state/profile-store';

export default function SubscriptionCancelScreen() {
  useTrackScreen('SubscriptionCancel');
  const router = useRouter();
  const colors = useThemeColors();
  const userRole = useProfileStore((s) => s.userRole);

  // Track subscription canceled on mount (ref-guarded)
  const cancelTracked = React.useRef(false);
  useEffect(() => {
    if (cancelTracked.current) return;
    cancelTracked.current = true;
    track('subscription_canceled');
  }, []);

  useEffect(() => {
    // Redirect back to subscription page after a moment
    const timer = setTimeout(() => {
      const subscriptionPath = userRole === 'investor'
        ? '/(investor)/investor-subscription'
        : '/(startup)/startup-subscription';
      router.replace(subscriptionPath);
    }, 2000);

    return () => clearTimeout(timer);
  }, [router, userRole]);

  return (
    <View
      style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.surface.primary,
        padding: 24,
      }}
    >
      <View
        style={{
          width: 80,
          height: 80,
          borderRadius: 40,
          backgroundColor: colors.amber.light,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <X size={40} color={colors.warning} />
      </View>
      <Text
        style={{
          fontSize: 24,
          fontWeight: '700',
          color: colors.text.primary,
          marginBottom: 8,
        }}
      >
        Checkout Cancelled
      </Text>
      <Text
        style={{
          fontSize: 16,
          color: colors.text.secondary,
          textAlign: 'center',
        }}
      >
        No worries! Redirecting back to subscription options...
      </Text>
    </View>
  );
}
